export interface PreparedImage {
  file: File
  widthPx?: number
  heightPx?: number
}

const MAX_SIDE_PX = 1920
const QUALITY = 0.82
// GIF may be animated, SVG is vector — re-encoding would break them.
const SKIP_TYPES = new Set(['image/gif', 'image/svg+xml'])

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality))
}

function replaceExtension(name: string, extension: string): string {
  const base = name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name
  return `${base || 'photo'}.${extension}`
}

/**
 * Downscales a photo/screenshot to MAX_SIDE_PX and re-encodes it as WebP (JPEG
 * where WebP encoding is unsupported). Teachers mostly paste PNG screenshots of
 * lesson videos: ~560 kB each as PNG vs ~100 kB as lossy. Any failure (e.g. a
 * HEIC the browser cannot decode) returns the original file untouched.
 */
export async function prepareImageForUpload(file: File): Promise<PreparedImage> {
  if (!file.type.startsWith('image/') || SKIP_TYPES.has(file.type) || typeof createImageBitmap !== 'function') {
    return { file }
  }

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return { file }
  }

  try {
    const scale = Math.min(1, MAX_SIDE_PX / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const original: PreparedImage = { file, widthPx: bitmap.width, heightPx: bitmap.height }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return original
    context.drawImage(bitmap, 0, 0, width, height)

    let blob = await canvasToBlob(canvas, 'image/webp', QUALITY)
    if (!blob || blob.type !== 'image/webp') {
      // JPEG has no alpha: flatten transparent screenshots onto white.
      context.globalCompositeOperation = 'destination-over'
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
      blob = await canvasToBlob(canvas, 'image/jpeg', QUALITY)
    }
    if (!blob || (scale === 1 && blob.size >= file.size)) return original

    const extension = blob.type === 'image/webp' ? 'webp' : 'jpg'
    return {
      file: new File([blob], replaceExtension(file.name, extension), { type: blob.type, lastModified: file.lastModified }),
      widthPx: width,
      heightPx: height,
    }
  } catch {
    return { file }
  } finally {
    bitmap.close()
  }
}
